import React, { memo, useCallback } from 'react';
import { Button, Table } from 'antd';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import moment from 'moment';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { useSearchParams } from 'react-router-dom';
import Loader from '@components/Loader';
import { nonNull } from '@helpers/non-null';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { AssetItemDto } from '@api/Api';
import UserAvatar from '@components/UserAvatar';
import type { FolderWithItems } from '@pages/Campaigns/CampaignReview/utils';
import Status from '@components/Approval/Status';
import { download, downloadAll } from '@helpers/download';

import './ListView.less';

type ListViewProps = {
  onSelectFolder: (folderId: string) => void;
  folders: FolderWithItems[];
  assetsList: AssetItemDto[];
  isOpenFolder: boolean;
};

export default memo(function ListView({
  onSelectFolder,
  folders,
  assetsList,
  isOpenFolder
}: ListViewProps) {
  const [_, setSearchParams] = useSearchParams();

  const onAssetClick = useCallback(
    (asset: AssetItemDto) => {
      setSearchParams((prev) => {
        prev.set('assetId', asset.id);
        const version = nonNull(asset.asset).versions[0];
        prev.set('versionId', version.id);
        return prev;
      });
    },
    [setSearchParams]
  );

  const foldersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (name: string, { id, isPrivate }: FolderWithItems) => {
        return (
          <div className="list-view-folder">
            <div
              className="list-view-folder-icon"
              onClick={() => {
                onSelectFolder(id);
              }}
            >
              {isPrivate ? <PrivateFolderSvg /> : <FolderSvg />}
            </div>
            {name}
          </div>
        );
      }
    },
    {
      title: '',
      dataIndex: 'owner',
      key: 'owner',
      width: 190
    },
    {
      title: ' ',
      dataIndex: 'created',
      key: 'created',
      width: 190
    },
    {
      title: 'Modified',
      key: 'modified',
      dataIndex: 'modified',
      width: 160,
      render: (_id: string, folder: FolderWithItems) => {
        const modified = Math.max(
          ...folder.items.map((x) => new Date(x.modifiedAt).getTime())
        );
        return moment(modified).fromNow();
      }
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'id',
      width: 32,
      render: (_id: string, folder: FolderWithItems) => {
        const assetsToDownload = folder.items.filter(
          (asset) => !!asset.asset?.permissions.download
        );
        if (!assetsToDownload?.length) return null;
        return (
          <Button
            type="text"
            size="small"
            className="assets-list-btn-download"
            icon={<DownloadInvoiceSvg />}
            onClick={(e) => {
              e.stopPropagation();
              downloadAll(assetsToDownload, `${folder.name}.zip`, folder.id);
            }}
          />
        );
      }
    }
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (_name: string, { asset }: AssetItemDto) => {
        const preparePreview = assetPreviewThumbnails({
          url: asset?.versions[0].metadata.preview?.url || '',
          status: asset?.versions[0].metadata.preview?.status || 'ready',
          type: asset?.versions[0].metadata.type || '',
          size: 40,
          format: 'img'
        });
        return (
          <div className="list-view-asset">
            <div className="list-view-asset-icon">
              {preparePreview === 'pending' && (
                <Loader spinSize={40} isSpinning={true} />
              )}
              {preparePreview !== 'pending' &&
              typeof preparePreview === 'string' ? (
                <img src={preparePreview || ''} alt="" />
              ) : (
                preparePreview
              )}
            </div>
            <div className="list-view-asset-name">
              {asset?.versions?.[0]?.name}.{asset?.versions?.[0]?.extension}
              {!!asset?.approvalWorkflow && (
                <Status
                  showOverdueTag
                  status={asset.approvalWorkflow.status}
                  approvalWorkflowStatus={asset.approvalWorkflow.status}
                  approvalWorkflowIsPaused={asset.approvalWorkflow.isPaused}
                  deadline={asset.approvalWorkflow.deadline}
                />
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 190,
      render: (_id: string, { owner }: AssetItemDto) => {
        return (
          <UserAvatar
            isActive={true}
            size={40}
            src={owner?.user?.picture.url || ''}
            userEmail={owner?.user?.email || ''}
            userName={owner?.user?.name || ''}
          />
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      render: (createdAt: string) => {
        return moment(createdAt).format('MMM DD, YYYY');
      }
    },
    {
      title: 'Modified',
      key: 'modifiedAt',
      dataIndex: 'modifiedAt',
      width: 160,
      render: (modifiedAt: string) => {
        return moment(modifiedAt).fromNow();
      }
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'id',
      width: 32,
      render: (_id: string, asset: AssetItemDto) => {
        if (!asset.asset?.permissions.download) return null;
        return (
          <Button
            type="text"
            size="small"
            className="assets-list-btn-download"
            icon={<DownloadInvoiceSvg />}
            onClick={(e) => {
              e.stopPropagation();
              if (!asset?.asset) return;
              const { name, sourceUrl } = asset.asset.versions[0];
              download(sourceUrl, name);
            }}
          />
        );
      }
    }
  ];

  return (
    <div className="assets-list-view">
      {!!folders?.length && (
        <div className="assets-list-view-folders">
          <div className="assets-list-view-label">Folders</div>
          <Table
            pagination={false}
            columns={foldersColumns}
            dataSource={folders}
            onRow={({ id }) => ({
              onClick: () => {
                onSelectFolder(id);
              }
            })}
          />
        </div>
      )}
      {!!assetsList?.length && (
        <div className="assets-list-view-assets">
          <div className="assets-list-view-label">Assets</div>
          <Table
            pagination={false}
            columns={columns}
            dataSource={assetsList}
            rowKey={({ id }) => id}
            onRow={(asset) => ({
              onClick: () => {
                onAssetClick(asset);
              }
            })}
          />
        </div>
      )}
    </div>
  );
});
